<script setup>
/**
 * Modal —— 受控弹层（v-model:open）。ESC 关闭、可选 backdrop 关闭、打开时焦点陷阱。
 * 与业务无关：标题经 prop、正文/底部经 slot 注入。
 */

import { ref } from 'vue'
import { useFocusTrap } from '../composables/use-focus-trap.js'

const props = defineProps({
  /** 受控开关。 */
  open: { type: Boolean, default: false },
  /** 标题文案（可选；不传则不渲染标题栏文字）。 */
  title: { type: String, default: '' },
  /** 点击 backdrop 是否关闭。 */
  closeOnBackdrop: { type: Boolean, default: true },
  /** 对话框最大宽度。 */
  width: { type: String, default: '560px' },
})

const emit = defineEmits(['update:open', 'close'])

/** @type {import('vue').Ref<HTMLElement|null>} 对话框根元素，用于焦点陷阱。 */
const dialogEl = ref(null)

/**
 * 关闭：同步 v-model 并发 close 事件。
 * @returns {void}
 */
function close() {
  emit('update:open', false)
  emit('close')
}

/**
 * backdrop 点击关闭（仅当 closeOnBackdrop）。
 * @returns {void}
 */
function onBackdrop() {
  if (props.closeOnBackdrop) close()
}

// ESC 关闭 + Tab 焦点循环 + 打开聚焦 / 关闭还焦点，与 Drawer 共用同一份实现。
useFocusTrap(dialogEl, () => props.open, close)
</script>

<template>
  <Teleport to="body">
    <div
      v-if="open"
      class="drama-modal__backdrop"
      @click.self="onBackdrop"
    >
      <div
        ref="dialogEl"
        class="drama-modal__dialog"
        :style="{ maxWidth: width }"
        role="dialog"
        aria-modal="true"
        tabindex="-1"
      >
        <header
          v-if="title"
          class="drama-modal__header"
        >
          <h2 class="drama-modal__title">
            {{ title }}
          </h2>
          <button
            class="drama-modal__close"
            type="button"
            aria-label="Close"
            @click="close"
          >
            ×
          </button>
        </header>
        <div class="drama-modal__body">
          <slot />
        </div>
        <footer
          v-if="$slots.footer"
          class="drama-modal__footer"
        >
          <slot name="footer" />
        </footer>
      </div>
    </div>
  </Teleport>
</template>

<style scoped>
.drama-modal__backdrop {
  position: fixed;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: var(--drama-color-overlay);
  padding: var(--drama-space-lg);
  z-index: 1000;
}

.drama-modal__dialog {
  background-color: var(--drama-color-surface);
  color: var(--drama-color-text);
  border: 1px solid var(--drama-color-border);
  border-radius: var(--drama-radius-lg);
  box-shadow: var(--drama-shadow-lg);
  width: 100%;
  max-height: 90vh;
  overflow: auto;
  outline: none;
}

.drama-modal__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: var(--drama-space-lg) var(--drama-space-xl);
  border-bottom: 1px solid var(--drama-color-border);
}

.drama-modal__title {
  margin: 0;
  font-family: var(--drama-font-family-heading);
  font-size: var(--drama-font-size-lg);
  font-weight: var(--drama-font-weight-bold);
}

.drama-modal__close {
  background: transparent;
  border: none;
  color: var(--drama-color-text-muted);
  font-size: var(--drama-font-size-xl);
  line-height: 1;
  cursor: pointer;
  padding: 0 var(--drama-space-sm);
}

.drama-modal__body {
  padding: var(--drama-space-xl);
}

.drama-modal__footer {
  display: flex;
  justify-content: flex-end;
  gap: var(--drama-space-md);
  padding: var(--drama-space-lg) var(--drama-space-xl);
  border-top: 1px solid var(--drama-color-border);
}
</style>
